import './utils/cryptoUUID'
import './styles/index.css'

import { DeferredAnalytics } from '@providers/DeferredAnalytics'
import ErrorBoundary from '@components/shared/errors/ErrorBoundary.tsx'
import ScrollToTop from '@components/shared/ScrollToTop.tsx'
import { ReactQueryV4 } from '@providers/react-query'
import { LightweightProviders } from '@providers/LightweightProviders'
import { DeferredWeb3Wrapper } from '@providers/DeferredWeb3Wrapper'
import { AppUpdateBanner, AppUpdateProvider } from '@domain/app-update'
import { FirstTouchCapture } from '@domain/attribution'

/**
 * `AppUpdateProvider` wraps `ErrorBoundary`, and `AppUpdateBanner` renders as a
 * sibling of it rather than a child, on purpose.
 *
 * A stale bundle usually surfaces as a crash — a chunk that no longer exists, or a
 * component calling into an API shape the old code does not understand. If the
 * banner lived inside the boundary it would be unmounted along with the rest of the
 * tree at exactly the moment the user most needs a way out. Kept outside, the
 * "Force Refresh" prompt is still there on top of the error screen.
 */
export const App = ({ children }: { children: React.ReactNode }) => {
  return (
    <AppUpdateProvider>
      <ErrorBoundary>
        <FirstTouchCapture />
        <DeferredAnalytics>
          <ReactQueryV4>
            <LightweightProviders>
              <DeferredWeb3Wrapper>
                <>
                  <ScrollToTop />
                  {children}
                </>
              </DeferredWeb3Wrapper>
            </LightweightProviders>
          </ReactQueryV4>
        </DeferredAnalytics>
      </ErrorBoundary>

      <AppUpdateBanner />
    </AppUpdateProvider>
  )
}

export default App
